/**
 * CSVFilesTable Component
 * Renders CSV files table with multiple columns for PDF
 * Design: Technical/Engineering with focus on readability
 */

import React from 'react';
import { View, Text, Page } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { CSVFileEntry } from '@/types/common';
import { styles } from '../styles';
import { ParameterDefinition } from '../types';

interface CSVFilesTableProps {
  fileGroup: (CSVFileEntry & Record<string, unknown>)[];
  parameters: ParameterDefinition[];
  groupIndex: number;
  title: string;
  isNewPage?: boolean;
}

export const CSVFilesTable: React.FC<CSVFilesTableProps> = ({
  fileGroup,
  parameters,
  groupIndex,
  title,
  isNewPage = false,
}) => {
  const { t } = useTranslation('pdfReport');
  const isLastFile = (index: number) => index === fileGroup.length - 1;

  const tableContent = (
    <View style={styles.csvTable}>
      {/* Header Row */}
      <View style={styles.tableRow}>
        <View style={styles.csvTableColHeaderFirst}>
          <Text style={styles.csvTableCellHeader}>{t('csvTable.parameter')}</Text>
        </View>
        {fileGroup.map((file, fileIndex) => (
          <View
            key={`header-${groupIndex}-${fileIndex}`}
            style={[
              styles.csvTableColHeader,
              isLastFile(fileIndex) ? styles.csvTableColHeaderLast : {},
            ]}
          >
            <Text style={styles.csvTableCellHeader}>
              {String(
                file.file_name ||
                  file.bezeichnung ||
                  t('csvTable.fallbackFileName', { index: fileIndex + 1 })
              )}
            </Text>
          </View>
        ))}
      </View>

      {/* Parameter Rows */}
      {parameters.map((param, paramIndex) => (
        <View
          key={`param-${groupIndex}-${paramIndex}`}
          style={[
            styles.tableRow,
            paramIndex % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd,
          ]}
        >
          <View style={styles.csvTableColFirst}>
            <Text style={styles.csvTableCellLabel}>{param.label}</Text>
          </View>
          {fileGroup.map((file, fileIndex) => (
            <View
              key={`value-${groupIndex}-${paramIndex}-${fileIndex}`}
              style={[
                styles.csvTableCol,
                paramIndex % 2 === 0 ? styles.tableRowEven : styles.tableRowOdd,
                isLastFile(fileIndex) ? styles.csvTableColLast : {},
              ]}
            >
              <Text style={styles.csvTableCell}>
                {param.format
                  ? String(param.format(file[param.key as keyof typeof file] || ''))
                  : String(file[param.key as keyof typeof file] || '-')}
              </Text>
            </View>
          ))}
        </View>
      ))}
    </View>
  );

  if (isNewPage) {
    return (
      <Page size="A4" style={styles.page}>
        <Text style={styles.subtitle}>
          {t('csvTable.continuation', { title, index: groupIndex + 1 })}
        </Text>
        {tableContent}
        {/* Footer */}
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>Forecast Engine</Text>
          <Text
            style={styles.pageNumber}
            render={({ pageNumber, totalPages }) =>
              t('csvTable.pageOf', { pageNumber, totalPages })
            }
          />
        </View>
      </Page>
    );
  }

  return tableContent;
};
